import type { HttpClient } from 'services/api-client/client';
import type { CloudResourceApi } from '@env0/cloud-resource-service/api';
import { returnData } from 'services/api-client/client';

export type CloudCompassAssetQuery = { filter: CloudResourceApi.CloudCompassAssetFilter; q: string };
export type CloudCompassAssetCountsQuery = CloudCompassAssetQuery & { groupBy: string };
export type CloudCompassAssetsQuery = CloudCompassAssetQuery &
  Pick<CloudResourceApi.GetCloudCompassAssets.Request.QueryParams, 'sortBy' | 'sortDir'> & { page: number };

export type CloudCompassListQuery = CloudCompassAssetQuery & { sortBy?: string; sortDir?: 'asc' | 'desc' };
export type CloudCompassListParams = CloudCompassListQuery & { page: number; pageSize: number };
export type CloudCompassListScope = CloudResourceApi.CloudCompassListScope;

const toAssetQueryParams = ({ filter, q }: CloudCompassAssetQuery) => ({
  ...(Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  ...(q && { q })
});

export type CloudCompassListResponse = CloudResourceApi.CloudCompassListPage & {
  resources: (
    | CloudResourceApi.GetCloudCompassManagedResources.Resource
    | CloudResourceApi.GetCloudCompassUnmanagedResources.Resource
  )[];
};

const LIST_PATHS: Record<CloudResourceApi.CloudCompassListSurface, (id: string) => string> = {
  managed: id => `cloud/environments/${id}/managed-resources`,
  'environment-unmanaged': id => `cloud/environments/${id}/unmanaged-resources`,
  'unmanaged-environment': id => `cloud/unmanaged-environments/${id}/resources`,
  unmanaged: () => 'cloud/unmanaged/resources',
  orphaned: () => 'cloud/resource-lists',
  unattributed: () => 'cloud/resource-lists'
};

const isProjectPlacement = (
  surface: CloudResourceApi.CloudCompassListSurface
): surface is CloudResourceApi.CloudCompassProjectPlacement => surface === 'orphaned' || surface === 'unattributed';

const toListQueryParams = ({ filter, q, ...params }: CloudCompassListParams) => ({
  ...params,
  ...toAssetQueryParams({ filter, q })
});

type AssetPath = CloudResourceApi.CloudCompassAssetPath;

export type FilterValuesParams = Omit<
  CloudResourceApi.GetCloudCompassFilterValues.Request.QueryParams,
  'organizationId'
>;

const assetUrl = ({ resourceType, platformId }: AssetPath) => `cloud/assets/${resourceType}/${platformId}`;

const getForOrganization = <Response>(client: HttpClient, url: string, organizationId: string, params?: object) =>
  client.get<Response>(url, { params: { organizationId, ...params } }).then(returnData);

export default (client: HttpClient) => ({
  findCloudResources: async (
    body: CloudResourceApi.FindCloudResources.Request.Body
  ): Promise<CloudResourceApi.FindCloudResources.Response> =>
    client.post<CloudResourceApi.FindCloudResources.Response>('/cloud/resources', body).then(returnData),
  updateCloudResources: async (
    body: CloudResourceApi.UpdateCloudResources.Request.Body
  ): Promise<CloudResourceApi.UpdateCloudResources.Response> =>
    client.put<CloudResourceApi.UpdateCloudResources.Response>('/cloud/resources', body).then(returnData),
  startIacGeneration: async (body: CloudResourceApi.StartIacGeneration.Request.Body) =>
    client.post<CloudResourceApi.StartIacGeneration.Response>('/cloud/resources/generate-iac', body).then(returnData),
  getIacGenerationResult: async (jobId: string) =>
    client
      .get<CloudResourceApi.GetIacGenerationResult.Response>(`/cloud/resources/generate-iac/${jobId}`)
      .then(returnData),
  getChangeEventUrl: async (eventId: string) =>
    client.get<CloudResourceApi.GetChangeEventUrl.Response>(`cloud/resources/events/${eventId}`).then(returnData),
  getResourceChangeEvents: async (cloudResourceId: string) =>
    client
      .get<CloudResourceApi.GetResourceEvents.Response>(`cloud/resources/${cloudResourceId}/events`)
      .then(returnData),
  getResourceFullEvent: async (eventId: string) =>
    client.get<CloudResourceApi.GetEventById.Response>(`cloud/events/${eventId}`).then(returnData),
  getCloudCompassFilterValues: async (organizationId: string, dimension: string, params?: FilterValuesParams) =>
    getForOrganization<CloudResourceApi.GetCloudCompassFilterValues.Response>(
      client,
      `cloud/filters/${dimension}/values`,
      organizationId,
      params
    ),
  getCloudCompassAssetCounts: async (organizationId: string, query: CloudCompassAssetCountsQuery) =>
    client
      .get<CloudResourceApi.GetCloudCompassAssetCounts.Response>('cloud/assets/counts', {
        params: { organizationId, groupBy: query.groupBy, ...toAssetQueryParams(query) }
      })
      .then(returnData),
  getCloudCompassTree: async (organizationId: string, query?: CloudCompassAssetQuery) =>
    getForOrganization<CloudResourceApi.GetCloudCompassTree.Response>(
      client,
      'cloud/tree',
      organizationId,
      query && toAssetQueryParams(query)
    ),
  getCloudCompassUnmanagedAttribution: async (organizationId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassUnmanagedCounts.Response>(
      client,
      'cloud/unmanaged-attribution',
      organizationId
    ),
  getCloudCompassInsights: async (
    organizationId: string,
    scope: CloudResourceApi.GetCloudCompassInsights.Scope,
    distinct = false
  ) =>
    getForOrganization<CloudResourceApi.GetCloudCompassInsights.Response>(client, 'cloud/insights', organizationId, {
      scope,
      ...(distinct && { distinct: 'true' })
    }),
  listCloudCompassInsights: async (organizationId: string, query: CloudResourceApi.InsightListQuery) =>
    getForOrganization<CloudResourceApi.InsightListResponse>(
      client,
      'cloud/insights/list',
      organizationId,
      toAssetQueryParams(query)
    ),
  getCloudCompassInsight: async (organizationId: string, insightId: string) =>
    getForOrganization<CloudResourceApi.InsightDetails>(
      client,
      `cloud/insights/${encodeURIComponent(insightId)}`,
      organizationId
    ),
  silenceCloudCompassInsight: async (
    organizationId: string,
    insightId: string,
    body: CloudResourceApi.SilenceInsightBody
  ) =>
    client
      .post<CloudResourceApi.InsightDetails>(`cloud/insights/${encodeURIComponent(insightId)}/silence`, body, {
        params: { organizationId }
      })
      .then(returnData),
  unsilenceCloudCompassInsight: async (organizationId: string, insightId: string) =>
    client
      .delete<CloudResourceApi.InsightDetails>(`cloud/insights/${encodeURIComponent(insightId)}/silence`, {
        params: { organizationId }
      })
      .then(returnData),
  getCloudCompassUnmanagedEnvironments: async (organizationId: string) =>
    getForOrganization<CloudResourceApi.GetCompassUnmanagedEnvironments.Response>(
      client,
      'cloud/unmanaged-environments',
      organizationId
    ),
  getCloudCompassList: async <Response = CloudCompassListResponse>(
    organizationId: string,
    { surface, id }: CloudCompassListScope,
    params: CloudCompassListParams
  ) =>
    getForOrganization<Response>(client, LIST_PATHS[surface](id ?? ''), organizationId, {
      ...toListQueryParams(params),
      ...(isProjectPlacement(surface) && { surface, id })
    }),
  getCloudCompassListGroups: async (
    organizationId: string,
    scope: CloudCompassListScope,
    groupBy: string,
    params: Omit<CloudCompassListParams, 'sortBy' | 'sortDir'>
  ) =>
    getForOrganization<CloudResourceApi.GetCloudCompassListGroups.Response>(
      client,
      'cloud/resource-lists/groups',
      organizationId,
      { ...scope, groupBy, ...toListQueryParams(params) }
    ),
  getCloudCompassListValues: async (
    organizationId: string,
    scope: CloudCompassListScope,
    dimension: string,
    q: string
  ) =>
    getForOrganization<CloudResourceApi.GetCloudCompassListValues.Response>(
      client,
      'cloud/resource-lists/values',
      organizationId,
      { ...scope, dimension, ...(q && { q }) }
    ),
  startCloudCompassListExport: async (
    organizationId: string,
    body: CloudResourceApi.StartCloudCompassListExport.Request.Body
  ) =>
    client
      .post<CloudResourceApi.StartCloudCompassListExport.Response>('cloud/resource-lists/exports', body, {
        params: { organizationId }
      })
      .then(returnData),
  getCloudCompassListExport: async (organizationId: string, exportId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassListExport.Response>(
      client,
      `cloud/resource-lists/exports/${exportId}`,
      organizationId
    ),
  getCloudCompassUnmanagedRemainderTotal: async (organizationId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassUnmanagedRemainder.Response>(
      client,
      'cloud/unmanaged/resources',
      organizationId,
      { totalOnly: 'true' }
    ),
  getCloudCompassAssetDetails: async (organizationId: string, path: AssetPath) =>
    getForOrganization<CloudResourceApi.GetCloudCompassAssetDetails.Response>(
      client,
      `${assetUrl(path)}/details`,
      organizationId
    ),
  readCloudCompassMetadataRows: async (organizationId: string, path: AssetPath, rows: AssetPath[]) =>
    client
      .post<CloudResourceApi.ReadCloudCompassMetadataRows.Response>(
        `${assetUrl(path)}/metadata-rows`,
        { rows },
        { params: { organizationId } }
      )
      .then(returnData),
  // A walk that runs out of time has already taken about 40 s, so a retry only makes the drawer wait longer.
  getCloudCompassAssetRelated: async (organizationId: string, path: AssetPath) =>
    client
      .get<CloudResourceApi.GetCloudCompassAssetRelated.Response>(`${assetUrl(path)}/related`, {
        params: { organizationId },
        skipRetryOnTimeout: true
      })
      .then(returnData),
  checkCloudCompassManagedStatus: async (organizationId: string, resources: AssetPath[]) =>
    client
      .post<CloudResourceApi.CheckCloudCompassManagedStatus.Response>(
        'cloud/assets/managed-status',
        { resources },
        { params: { organizationId } }
      )
      .then(returnData),
  getCloudCompassAssets: async (organizationId: string, { filter, q, ...params }: CloudCompassAssetsQuery) =>
    client
      .get<CloudResourceApi.GetCloudCompassAssets.Response>('cloud/assets', {
        params: { organizationId, ...params, ...toAssetQueryParams({ filter, q }) }
      })
      .then(returnData)
});
